/**
 * @fabric-msft/localization
 *
 * Provides type-safe, lazy-loaded localized strings for Fabric UX components.
 *
 * @example
 * ```typescript
 * import { loadStrings, getString, formatString } from '@fabric-msft/localization';
 *
 * // Load strings at app startup
 * await loadStrings('es');
 *
 * // Simple string lookup
 * const label = getString('SearchBox_Clear');
 *
 * // With placeholder interpolation
 * const status = formatString('Pagination_Status', 5, 100);
 * // "Showing 5 of 100 items" (if string is "Showing {0} of {1} items")
 * ```
 */
import { localeLoaders, availableLocales } from "./locales/index.js";
export { availableLocales };
let currentStrings = null;
let currentLocale = null;
/**
 * Load localized strings for a specific locale.
 * Dynamically imports only the requested locale's strings.
 *
 * @param locale - The locale code (e.g., 'en', 'es', 'zh-Hans')
 * @throws Error if the locale is not available
 *
 * @example
 * ```typescript
 * await loadStrings('es');
 * await loadStrings('zh-Hans');
 * ```
 */
export async function loadStrings(locale) {
    const loader = localeLoaders[locale];
    if (!loader) {
        throw new Error(`Locale "${locale}" is not available. Available locales: ${availableLocales.join(", ")}`);
    }
    const module = await loader();
    setStrings(locale, module.strings);
}
/**
 * Synchronously set the active localized strings without a dynamic import.
 *
 * Use this in environments that cannot execute the dynamic `import()` that
 * {@link loadStrings} relies on — for example SystemJS-based sandboxes such
 * as StackBlitz's `create-react-app` template. Pass a statically-imported
 * locale record from `@fabric-msft/localization/locales/<locale>`.
 *
 * @param locale - The locale code these strings correspond to.
 * @param strings - The full record of localized strings for the locale.
 *
 * @example
 * ```typescript
 * import { strings } from '@fabric-msft/localization/locales/en';
 * import { setStrings } from '@fabric-msft/localization';
 *
 * setStrings('en', strings);
 * ```
 */
export function setStrings(locale, strings) {
    currentStrings = strings;
    currentLocale = locale;
}
/**
 * Get the currently loaded locale code.
 *
 * @returns The loaded locale code, or null if no strings are loaded
 */
export function getLoadedLocale() {
    return currentLocale;
}
/**
 * Get a localized string by key.
 *
 * @param key - The string key (type-checked against available keys)
 * @returns The localized string, or the key itself if not found
 * @throws Error if strings have not been loaded
 *
 * @example
 * ```typescript
 * const label = getString('SearchBox_Clear');
 * ```
 */
export function getString(key) {
    if (!currentStrings) {
        // TODO: Hook this up to debug instead, Work Item: 2145212
        console.warn("Localized strings not loaded. Call loadStrings() first.");
        return key;
    }
    return currentStrings[key] ?? key;
}
export function formatString(key, ...args) {
    let str = getString(key);
    // Detect Record overload: single arg that's a plain object
    if (args.length === 1 && typeof args[0] === "object" && args[0] !== null) {
        return formatStringTemplate(str, args[0]);
    }
    for (let i = 0; i < args.length; i++) {
        str = str.replace(new RegExp(`\\{${i}\\}`, "g"), () => String(args[i]));
    }
    return str;
}
export function formatStringTemplate(template, values) {
    return Object.entries(values).reduce((label, [token, value]) => label.split(`{${token}}`).join(value), template);
}
export function overrideLocaleLoader(locale, loader) {
    localeLoaders[locale] = loader;
}
export function overrideLocaleLoaders(loaders) {
    for (const locale of Object.keys(loaders)) {
        overrideLocaleLoader(locale, loaders[locale]);
    }
}
